import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Department, Membership, Person, ServiceTeam } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { SearchSelect } from '../../shared/ui/SearchSelect';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { ASSIGNMENT_STATUS } from './labels';

type MemberRow = Membership & { person: Person | null };

/** Equipo de un culto (Motor 8): quién sirve, con qué función, y quién no está disponible. */
export function TeamPage() {
  const { serviceId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const team = useAsync(
    (signal) => (churchId ? api.get<ServiceTeam>(`/assignments/churches/${churchId}/services/${serviceId}`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId, serviceId],
  );
  // Dónde puedo asignar: toda la Iglesia o solo mis Departamentos (el backend vuelve a decidir).
  const manage = me.permissions.filter((p) => p.permission === 'assignments.manage');
  const churchWide = manage.some((p) => p.scope === 'church' || p.scope === 'platform');
  const myDepartments = new Set(manage.flatMap((p) => (p.scope === 'department' && p.departmentId ? [p.departmentId] : [])));
  const canAssign = churchWide || myDepartments.size > 0;

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page">
      <p className="small">
        <Link to={`/app/cultos/${serviceId}`}>← Programa del culto</Link>
      </p>
      {team.status === 'loading' ? <Loading /> : null}
      {team.status === 'error' ? <ErrorState error={team.error} onRetry={team.reload} /> : null}
      {team.status === 'ready' ? (
        <>
          <header className="page__header">
            <h1>Equipo</h1>
            <p className="muted">
              {team.data.occasion.title} · {formatDayHeading(new Date(team.data.occasion.startsAt))} · {formatTime(team.data.occasion.startsAt)}
            </p>
          </header>

          <Card title="Quién sirve">
            {team.data.assignments.length === 0 ? <EmptyState title="Aún no hay nadie asignado." /> : null}
            <ul className="list">
              {team.data.assignments.map((a) => (
                <AssignmentRow
                  key={a.id}
                  churchId={churchId}
                  assignment={a}
                  canCancel={churchWide || (a.departmentId !== null && myDepartments.has(a.departmentId))}
                  onChange={team.reload}
                />
              ))}
            </ul>
          </Card>

          {team.data.unavailable.length > 0 ? (
            <Card title="No disponibles ese día">
              <ul className="list">
                {team.data.unavailable.map((u) => (
                  <li key={u.personId} className="list__item">
                    <strong>{u.name}</strong>
                    {u.note ? <span className="muted small">{u.note}</span> : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {canAssign && team.data.occasion.status !== 'canceled' && new Date(team.data.occasion.endsAt) > new Date() ? (
            <AssignForm churchId={churchId} serviceId={serviceId} churchWide={churchWide} myDepartments={myDepartments} onAssigned={team.reload} />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function AssignmentRow({ churchId, assignment: a, canCancel, onChange }: { churchId: string; assignment: ServiceTeam['assignments'][number]; canCancel: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/assignments/churches/${churchId}/assignments/${a.id}/cancel`);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <li className="list__item service">
      <div className="service__main">
        <strong>{a.name}</strong>
        <span className="muted small">
          {a.role}
          {a.departmentName ? ` · ${a.departmentName}` : ''}
        </span>
        {a.status === 'declined' && a.declineReason ? <span className="muted small">Motivo: {a.declineReason}</span> : null}
      </div>
      <span className={`badge ${ASSIGNMENT_STATUS[a.status].tone}`}>{ASSIGNMENT_STATUS[a.status].label}</span>
      {canCancel && (a.status === 'requested' || a.status === 'accepted') ? (
        <Button variant="ghost" busy={busy} onClick={cancel}>
          Retirar
        </Button>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </li>
  );
}

function AssignForm({
  churchId,
  serviceId,
  churchWide,
  myDepartments,
  onAssigned,
}: {
  churchId: string;
  serviceId: string;
  churchWide: boolean;
  myDepartments: Set<string>;
  onAssigned: () => void;
}) {
  const me = useMe();
  const members = useAsync(
    (signal) =>
      me.can('members.read')
        ? api.get<{ items: MemberRow[] }>(`/multichurch/churches/${churchId}/memberships?status=active`, signal).then((r) => r.items)
        : Promise.resolve(null),
    [churchId],
  );
  const departments = useAsync(
    (signal) => api.get<{ items: Department[] }>(`/organization/churches/${churchId}/departments`, signal).then((r) => r.items.filter((d) => churchWide || myDepartments.has(d.id))),
    [churchId],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    if (!form.get('personId')) return setError('Escribe el nombre de la persona y elígela de la lista.');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/assignments/churches/${churchId}/services/${serviceId}/assignments`, {
        personId: String(form.get('personId')),
        role: String(form.get('role')),
        departmentId: String(form.get('departmentId') || '') || null,
      });
      formEl.reset();
      onAssigned();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (members.status === 'ready' && members.data === null) {
    return (
      <Card title="Asignar">
        <Alert tone="info">Para elegir personas necesitas ver la lista de miembros; pídeselo a la administración de la iglesia.</Alert>
      </Card>
    );
  }

  return (
    <Card title="Asignar">
      {members.status === 'loading' || departments.status === 'loading' ? <Loading /> : null}
      {members.status === 'ready' && members.data && departments.status === 'ready' ? (
        <form className="form" onSubmit={onSubmit} noValidate>
          {error ? <Alert>{error}</Alert> : null}
          <div className="field">
            <label htmlFor="personId">Persona</label>
            <SearchSelect
              id="personId"
              name="personId"
              required
              placeholder="Escribe el nombre…"
              options={members.data.map((m) => ({ value: m.person_id, label: m.person ? `${m.person.givenName} ${m.person.familyName}`.trim() : 'Persona' }))}
            />
          </div>
          <Field label="Función" name="role" required minLength={2} maxLength={80} placeholder="Guitarra, ujier, sonido…" />
          <div className="field">
            <label htmlFor="departmentId">Ministerio</label>
            <select id="departmentId" name="departmentId" defaultValue={churchWide ? '' : [...myDepartments][0]}>
              {churchWide ? <option value="">Sin ministerio</option> : null}
              {departments.data.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="secondary" busy={busy}>
            Asignar
          </Button>
        </form>
      ) : null}
    </Card>
  );
}
